import React from 'react';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { flow } from 'lodash';

import * as Types from 'types';

import { PlaylistContainer } from 'playlists/PlaylistContainer';
import { withPlayerDispatcher } from 'player/withPlayerDispatcher';
import { setAddArtworkOpen, setCurrentPlaylist } from 'features/playlists/actions';
import { PlaylistToolbar } from 'playlists/PlaylistToolbar';
import { isPlaylistEditable } from 'features/playlists/helpers';

type StateProps = {
  playlistsById: Types.RootState['playlists']['byId'];
  currentPlaylist?: Types.Playlist;
  currentUserId?: number;
};

type OwnProps = {
  playlistId: string;
  editMode?: boolean;
};

type DispatchProps = {
  setCurrentPlaylist: typeof setCurrentPlaylist;
  setAddArtworkOpen: typeof setAddArtworkOpen;
};

type HOCProps = RouteComponentProps<{
  id: string;
}>;

type Props = StateProps & DispatchProps & OwnProps & HOCProps;

class SelectedPlaylist extends React.Component<Props> {
  componentDidMount() {
    this.onPlaylistIdPropChange();
  }

  componentDidUpdate(prevProps: Props) {
    const playlistIdChanged = this.props.playlistId !== prevProps.playlistId;
    if (playlistIdChanged) {
      this.onPlaylistIdPropChange();
    }
  }

  onPlaylistIdPropChange() {
    const playlist = this.props.playlistsById[this.props.playlistId];
    if (playlist) {
      this.setCurrentPlaylist(playlist);
    } else {
      this.props.history.push(Routes.vault_playlists_path());
    }
  }

  /**
   * Checks if current playlist needs
   * to be changed before changing it.
   */
  setCurrentPlaylist(newPlaylist: Types.Playlist) {
    const { currentPlaylist } = this.props;

    if (!newPlaylist && currentPlaylist) {
      this.props.setCurrentPlaylist(undefined);
      return;
    }

    if (newPlaylist) {
      const currentPlaylistChanged = currentPlaylist && currentPlaylist.id !== newPlaylist.id;

      if (!currentPlaylist || currentPlaylistChanged) {
        this.props.setCurrentPlaylist(newPlaylist);
      }
    }
  }

  render() {
    const { currentPlaylist, editMode, currentUserId } = this.props;

    if (!currentPlaylist) {
      return null; // Wait until loaded.
    }

    const playlistIsEditable = isPlaylistEditable(currentPlaylist, this.props.currentUserId || 0);

    return (
      <div className='selected-playlist-container'>
        <PlaylistToolbar playlist={currentPlaylist} currentUserId={currentUserId || 0} />
        {editMode && playlistIsEditable && (
          <button
            onClick={() => this.props.setAddArtworkOpen(true)}
            className='d-lg-none btn btn--border-light btn--artwork-pill icon-thin-plus w-100 mb-1'
          >
            Add
          </button>
        )}
        <PlaylistContainer
          items={currentPlaylist.items}
          itemsPlaylistId={currentPlaylist.id}
          droppable={editMode}
          isEdit={editMode}
          playlistIsEditable={playlistIsEditable}
          showRemove={playlistIsEditable && editMode}
          showPlay
        />
      </div>
    );
  }
}

const mapStateToProps = ({ playlists, currentUser }: Types.RootState): StateProps => ({
  playlistsById: playlists.byId,
  currentPlaylist: playlists.current,
  currentUserId: currentUser.user?.id,
});

const mapDispatchToProps = {
  setCurrentPlaylist,
  setAddArtworkOpen,
};

export const SelectedPlaylistContainer = flow(
  withRouter,
  withPlayerDispatcher,
  connect(mapStateToProps, mapDispatchToProps),
)(SelectedPlaylist);
